<template>
  <div class="mapa-wrapper">
    <div id="mapContainerEquipamentos" class="fullHeight" />

    <div
      v-if="
        legendaSemColetaVisivel ||
        legendaPendenteVisivel ||
        legendaLigaDesligaVisivel ||
        legendaPressaoVisivel ||
        legendaNivelVisivel ||
        legendaCotaVisivel ||
        legendaDiametroVisivel
      "
      class="legendas-escala"
    >
      <div v-if="legendaDiametroVisivel" class="legenda-pressao">
        <div class="legenda-pressao__titulo">Diâmetro</div>
        <div
          v-for="stop in stopsDiametroLegenda"
          :key="'d-' + stop.valor"
          class="legenda-pressao__item"
        >
          <input
            type="color"
            class="legenda-pressao__swatch-cor"
            :value="stop.cor"
            :title="'Cor do diâmetro ' + stop.rotulo"
            @change="alterarCorDiametro(stop.valor, $event.target.value)"
          />
          <span>{{ stop.rotulo }}</span>
        </div>
      </div>
      <div v-if="legendaNivelVisivel" class="legenda-pressao">
        <div class="legenda-pressao__titulo">Nível<br />reservatório</div>
        <div
          v-for="stop in stopsNivelLegenda"
          :key="'n-' + stop[0]"
          class="legenda-pressao__item"
        >
          <span
            class="legenda-pressao__swatch"
            :style="{ backgroundColor: stop[1] }"
          ></span>
          <span>{{ stop[0] }}</span>
        </div>
      </div>
      <div v-if="legendaPressaoVisivel" class="legenda-pressao">
        <div class="legenda-pressao__titulo">Pressão</div>
        <div
          v-for="stop in stopsPressao"
          :key="'p-' + stop[0]"
          class="legenda-pressao__item"
        >
          <span
            class="legenda-pressao__swatch"
            :style="{ backgroundColor: stop[1] }"
          ></span>
          <span>{{ stop[0] }}</span>
        </div>
      </div>
      <div v-if="legendaCotaVisivel" class="legenda-pressao">
        <div class="legenda-pressao__cabecalho">
          <div class="legenda-pressao__titulo">Cota</div>
          <button
            v-if="temOverrideCota"
            type="button"
            class="legenda-pressao__restaurar"
            title="Voltar todas ao padrão"
            @click="restaurarCoresCota"
          >
            settings_backup_restore
          </button>
        </div>
        <div
          v-for="stop in stopsCotaLegenda"
          :key="'c-' + stop.indice + '-' + stop.rotulo"
          class="legenda-pressao__item"
        >
          <input
            type="color"
            class="legenda-pressao__swatch-cor"
            :value="stop.cor"
            :title="'Cor da cota ' + stop.rotulo"
            @change="alterarCorCota(stop.indice, $event.target.value)"
          />
          <span>{{ stop.rotulo }}</span>
          <button
            v-if="stop.override"
            type="button"
            class="legenda-pressao__restaurar"
            title="Voltar esta cor ao padrão"
            @click="restaurarCorCota(stop.indice)"
          >
            settings_backup_restore
          </button>
        </div>
      </div>
      <div v-if="legendaLigaDesligaVisivel" class="legenda-pressao">
        <div class="legenda-pressao__titulo">Tensão / Corrente</div>
        <div
          v-for="item in legendaLigaDesliga"
          :key="item[1]"
          class="legenda-pressao__item"
        >
          <span
            class="legenda-pressao__swatch"
            :style="{ backgroundColor: item[0] }"
          ></span>
          <span>{{ item[1] }}</span>
        </div>
      </div>
      <div
        v-if="legendaSemColetaVisivel || legendaPendenteVisivel"
        class="legenda-pressao"
      >
        <div v-if="legendaSemColetaVisivel" class="legenda-pressao__item">
          <span
            class="legenda-pressao__swatch"
            :style="{ backgroundColor: legendaSemColeta[0] }"
          ></span>
          <span>{{ legendaSemColeta[1] }}</span>
        </div>
        <div v-if="legendaPendenteVisivel" class="legenda-pressao__item">
          <span
            class="legenda-pressao__onda"
            :style="{ borderColor: legendaPendente[0] }"
          ></span>
          <span>{{ legendaPendente[1] }}</span>
        </div>
      </div>
    </div>

    <div class="basemap-toggle">
      <select
        class="basemap-toggle__select"
        :value="estilo"
        title="Estilo do mapa"
        @change="trocarBasemap($event.target.value)"
      >
        <option v-for="e in estilos" :key="e.value" :value="e.value">
          {{ e.label }}
        </option>
      </select>
    </div>
  </div>
</template>

<script>
import DOMPurify from 'dompurify'
import mapboxgl from 'mapbox-gl'
import moment from 'moment'

import { obterDescricaoFeicao } from '@/utils/mapa-camadas'
import {
  ehChaveDiametro,
  escalaDiametroNasCamadas,
  expressaoCorDiametro,
  formatarDiametro,
} from '@/utils/mapa-escala-diametro'
import {
  CHAVE_JUNCTIONS,
  expressaoCorCota,
  faixaCotaNasCamadas,
  STOPS_NIVEL,
  STOPS_PRESSAO,
  stopsCota,
} from '@/utils/mapa-escala-pressao'
import {
  coresCotaSalvas,
  coresDiametroSalvas,
  corParaHex,
  gravarCorDiametro,
  gravarCoresCota,
  restaurarPadrao,
  restaurarStopCota,
} from '@/utils/mapa-escalas-cores-prefs'
import { chaveUsaIcone, ICONE_POR_CHAVE } from '@/utils/mapa-icones'
import { MAPBOX_LOCALE_PT } from '@/utils/mapbox-locale'
import {
  coletaSeRecente,
  COR_HUB,
  COR_MARCADOR_NEUTRO,
  ID_ICONE_HUB_NEUTRO,
  LEGENDA_LIGA_DESLIGA,
  LEGENDA_PENDENTE,
  LEGENDA_SEM_COLETA,
  obterIconeSensor,
  obterIdImagemSensor,
  registrarIconesSensores,
} from '@/utils/sensorMapaIcons'

import environment from '../../environment'

// Centro genérico (Brasil) só como fallback: assim que houver equipamentos ou
// camadas, o mapa dá fit no conteúdo. Nada de coordenada de cidade fixa aqui.
const CENTRO_PADRAO = [-51.9253, -14.235]
const ZOOM_PADRAO = 3.5

// Mesmos estilos (e nomes) usados no EpanetHeatmapIndex; "Padrão" é o default.
// `escuro` marca os basemaps de fundo escuro, para o casing adaptar a cor.
const ESTILO_PADRAO = 'mapbox://styles/mapbox/streets-v12'
const ESTILOS = [
  { label: 'Padrão', value: ESTILO_PADRAO },
  { label: 'Escuro', value: 'mapbox://styles/mapbox/dark-v10', escuro: true },
  {
    label: 'Satélite',
    value: 'mapbox://styles/mapbox/satellite-streets-v12',
    escuro: true,
  },
  {
    label: 'Navegação escuro',
    value: 'mapbox://styles/mapbox/navigation-preview-night-v4',
    escuro: true,
  },
  { label: 'Relevo', value: 'mapbox://styles/mapbox/outdoors-v12' },
]

// Casing/halo: branco sobre basemap claro, escuro sobre basemap escuro — é o
// contorno que separa a cor da camada do fundo, então precisa contrastar com ele.
const CASING_CLARO = '#ffffff'
const CASING_ESCURO = '#1a1a19'

// Ícones de seção (válvula/reservatório): rasterizamos o SVG neste tamanho (px)
// e escalamos com icon-size — grande o bastante pra ficar nítido em telas HiDPI.
const ICONE_TAMANHO = 64
const ICONE_ESCALA = 0.45

// @nazka/map-gl-js-spiderfy só mantém um cluster aberto por vez
// (spiderfy() chama _clearSpiderifiedCluster). Para mostrar todos os
// nós folha no load, desenhamos o spider expandido em GeoJSON.

const SOURCE_LEGS = 'sensores-mapa-legs'
const SOURCE_LEAVES = 'sensores-mapa-leaves'
const SOURCE_HUBS = 'sensores-mapa-hubs'
const LAYER_LEGS = 'sensores-mapa-legs-layer'
const LAYER_LEAVES = 'sensores-mapa-leaves-layer'
const LAYER_HUBS = 'sensores-mapa-hubs-layer'
const LAYER_HUB_ICONS = 'sensores-mapa-hubs-icon-layer'
const LAYER_HUB_LABELS = 'sensores-mapa-hubs-label-layer'

const SOURCE_BUSCA_DESTAQUE = 'busca-destaque'
const LAYER_BUSCA_DESTAQUE_FILL = 'busca-destaque-fill'
const LAYER_BUSCA_DESTAQUE_CASING = 'busca-destaque-line-casing'
const LAYER_BUSCA_DESTAQUE_LINE = 'busca-destaque-line'
const DESTAQUE_BUSCA_MS = 2500

const CIRCLE_SPIRAL_SWITCHOVER = 16
const CIRCLE_RADIUS_MIN = 40
const CIRCLE_POPUP_SLOT_PX = 52
const CIRCLE_RAIO_LONGO_EXTRA = 40
const SPIRAL_LEG_LENGTH_START = 44
const SPIRAL_LEG_LENGTH_FACTOR = 5
const SPIRAL_LEAVES_SEPARATION = 52

function emptyCollection() {
  return { type: 'FeatureCollection', features: [] }
}

function popupAnchorFromOffset(dx, dy) {
  if (dx === 0 && dy === 0) {
    return 'bottom'
  }

  const deg = ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360

  if (deg >= 337.5 || deg < 22.5) return 'left'
  if (deg < 67.5) return 'top-left'
  if (deg < 112.5) return 'top'
  if (deg < 157.5) return 'top-right'
  if (deg < 202.5) return 'right'
  if (deg < 247.5) return 'bottom-right'
  if (deg < 292.5) return 'bottom'
  return 'bottom-left'
}

export default {
  name: 'EquipamentoMapa',
  props: {
    localId: {
      type: [Number, String],
      default: null,
    },
    equipamentos: {
      type: Array,
      required: true,
    },
    equipamentosComAcaoPendente: {
      type: Array,
      default: () => [],
    },
    // Camadas KML: [{ id, nome, cor, visivel, geojson, versao }]
    camadas: {
      type: Array,
      default: () => [],
    },
    valoresPorSensorId: {
      type: Object,
      default: () => ({}),
    },
    iconesSensorVisiveis: {
      type: Object,
      default: () => ({}),
    },
    equipamentosVisiveis: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      map: null,
      mapLoaded: false,
      camadaVersoes: {},
      camadaCores: {},
      fitFeito: false,
      estilo: ESTILO_PADRAO,
      iconesProntos: false,
      spiderItems: [],
      lastCameraKey: null,
      leafCoordsPorSensor: {},
      aranhasRecolhidas: {},
      escalaCota: null,
      escalaDiametro: null,
      escalasCoresRev: 0,
    }
  },
  computed: {
    estilos() {
      return ESTILOS
    },
    corCasing() {
      return this.basemapEscuro ? CASING_ESCURO : CASING_CLARO
    },
    basemapEscuro() {
      const estilo = ESTILOS.find((e) => e.value === this.estilo)
      return !!(estilo && estilo.escuro)
    },
    stopsPressao() {
      return STOPS_PRESSAO.slice().reverse()
    },
    stopsNivelLegenda() {
      return STOPS_NIVEL.slice().reverse()
    },
    coresCotaPadrao() {
      return STOPS_PRESSAO.map((stop) => corParaHex(stop[1]))
    },
    coresCotaEfetivas() {
      if (this.escalasCoresRev < 0) {
        return this.coresCotaPadrao
      }
      return coresCotaSalvas() || this.coresCotaPadrao
    },
    temOverrideCota() {
      return this.escalasCoresRev >= 0 && coresCotaSalvas() != null
    },
    stopsCotaLegenda() {
      if (!this.escalaCota) {
        return []
      }
      const cores = this.coresCotaEfetivas
      const stops = stopsCota(this.escalaCota.min, this.escalaCota.max, cores)
      if (stops.length === 1) {
        const indice = Math.floor((cores.length - 1) / 2)
        const cor = corParaHex(stops[0][1])
        return [
          {
            rotulo: this.formatarCota(stops[0][0]),
            cor,
            indice,
            override: cor !== this.coresCotaPadrao[indice],
          },
        ]
      }
      return stops
        .slice()
        .reverse()
        .map(([valor, cor], i) => {
          const indice = stops.length - 1 - i
          const hex = corParaHex(cor)
          return {
            rotulo: this.formatarCota(valor),
            cor: hex,
            indice,
            override: hex !== this.coresCotaPadrao[indice],
          }
        })
    },
    stopsDiametroLegenda() {
      if (!this.escalaDiametro || this.escalasCoresRev < 0) {
        return []
      }
      return this.escalaDiametro
        .slice()
        .reverse()
        .map(([valor, cor]) => ({
          valor,
          rotulo: formatarDiametro(valor),
          cor: corParaHex(cor),
        }))
    },
    legendaCotaVisivel() {
      return (
        !!this.escalaCota &&
        this.camadas.some(
          (c) => c.chave === CHAVE_JUNCTIONS && c.visivel && c.geojson
        )
      )
    },
    legendaDiametroVisivel() {
      return (
        !!this.escalaDiametro &&
        this.camadas.some(
          (c) => ehChaveDiametro(c.chave) && c.visivel && c.geojson
        )
      )
    },
    legendaSemColeta() {
      return LEGENDA_SEM_COLETA
    },
    legendaPendente() {
      return LEGENDA_PENDENTE
    },
    legendaLigaDesliga() {
      return LEGENDA_LIGA_DESLIGA
    },
    legendaSemColetaVisivel() {
      return (
        this.equipamentosVisiveis &&
        Object.values(this.iconesSensorVisiveis).some((v) => v === true)
      )
    },
    legendaPendenteVisivel() {
      return (
        this.equipamentosVisiveis && this.equipamentosComAcaoPendente.length > 0
      )
    },
    legendaPressaoVisivel() {
      return (
        this.equipamentosVisiveis && this.iconesSensorVisiveis.speed === true
      )
    },
    legendaNivelVisivel() {
      return (
        this.equipamentosVisiveis &&
        this.iconesSensorVisiveis.oil_barrel === true
      )
    },
    legendaLigaDesligaVisivel() {
      return (
        this.equipamentosVisiveis &&
        (this.iconesSensorVisiveis.V === true ||
          this.iconesSensorVisiveis.A === true)
      )
    },
  },
  watch: {
    equipamentos() {
      this.rebuildSpiders()
    },
    equipamentosComAcaoPendente() {
      this.rebuildSpiders()
    },
    camadas: {
      deep: true,
      handler() {
        if (this.mapLoaded) {
          this.syncCamadas()
          this.fitContentSeNecessario()
        }
      },
    },
    valoresPorSensorId: {
      deep: true,
      handler() {
        this.atualizarHtmlSensorPopups()
        this.atualizarIconesSensorFolhas()
      },
    },
    iconesSensorVisiveis: {
      deep: true,
      handler() {
        this.rebuildSpiders()
      },
    },
    equipamentosVisiveis() {
      this.rebuildSpiders()
    },
  },
  created() {
    // Fora do data() para o Vue não observar instâncias do Mapbox.
    this.sensorPopups = {}
    this.hubHoverPopup = null
    this.hubHoverEquipamentoId = null
    this.camadaPopup = null
    this.buscaDestaqueMarker = null
    this.buscaDestaqueTimer = null
    this.buscaDestaqueOnMoveEnd = null
    this.buscaDestaqueSeq = 0
    this.buscaHubPopup = false
    this.pendenteOndaMarkers = {}
  },
  mounted() {
    mapboxgl.accessToken = environment.MAPBOXGL_ACCESSTOKEN

    const map = new mapboxgl.Map({
      container: 'mapContainerEquipamentos', // container ID
      style: this.estilo, // style URL
      center: CENTRO_PADRAO, // starting position [lng, lat]
      zoom: ZOOM_PADRAO, // starting zoom
      locale: MAPBOX_LOCALE_PT,
    })
    map.addControl(new mapboxgl.NavigationControl())

    this.map = map

    map.on('load', async () => {
      map.resize()

      // Registra as imagens dos ícones antes de desenhar as camadas: um symbol
      // layer que referencia uma imagem ainda não adicionada não renderiza.
      await this.carregarIcones()

      await registrarIconesSensores(map)
      this.iconesProntos = true
      this.mapLoaded = true
      this.syncCamadas()
      this.ensureLayers()
      this.bindMapEvents()
      this.rebuildSpiders()
      this.trazerSpidersParaFrente()
      this.fitContentSeNecessario()
    })

    this.coletaFrescaInterval = setInterval(() => {
      this.atualizarHtmlSensorPopups()
      this.atualizarIconesSensorFolhas()
    }, 5 * 60 * 1000)

    map.on('click', this.onMapaClick)
  },
  beforeDestroy() {
    clearInterval(this.coletaFrescaInterval)
    this.limparDestaqueBusca()
    this.removerOndasPendente()
    this.removerCamadaPopup()
    this.esconderHubHoverPopup()
    this.removerSensorPopups()
    this.map?.off('render', this.onMapRender)
    this.map?.off('move', this.onMapRender)
    this.map?.off('resize', this.onMapRender)
    this.map?.remove()
  },
  methods: {
    bindMapEvents() {
      this.map.on('render', this.onMapRender)
      this.map.on('move', this.onMapRender)
      this.map.on('resize', this.onMapRender)

      this.map.on('click', LAYER_LEAVES, this.handleLeafClick)
      this.map.on('click', LAYER_HUBS, this.handleHubClick)
      this.map.on('click', LAYER_HUB_ICONS, this.handleHubClick)

      this.map.on('mouseenter', LAYER_LEAVES, () => {
        this.map.getCanvas().style.cursor = 'pointer'
      })
      this.map.on('mouseleave', LAYER_LEAVES, () => {
        this.map.getCanvas().style.cursor = ''
      })
      this.map.on('mouseenter', LAYER_HUBS, this.handleHubEnter)
      this.map.on('mousemove', LAYER_HUBS, this.handleHubMove)
      this.map.on('mouseleave', LAYER_HUBS, this.handleHubLeave)
      this.map.on('mouseenter', LAYER_HUB_ICONS, this.handleHubEnter)
      this.map.on('mousemove', LAYER_HUB_ICONS, this.handleHubMove)
      this.map.on('mouseleave', LAYER_HUB_ICONS, this.handleHubLeave)
    },
    ensureLayers() {
      if (!this.map.getSource(SOURCE_LEGS)) {
        this.map.addSource(SOURCE_LEGS, {
          type: 'geojson',
          data: emptyCollection(),
        })
        this.map.addSource(SOURCE_LEAVES, {
          type: 'geojson',
          data: emptyCollection(),
        })
        this.map.addSource(SOURCE_HUBS, {
          type: 'geojson',
          data: emptyCollection(),
        })
      }

      if (!this.map.getLayer(LAYER_LEGS)) {
        this.map.addLayer({
          id: LAYER_LEGS,
          type: 'line',
          source: SOURCE_LEGS,
          paint: {
            'line-color': 'rgba(100, 100, 100, 0.7)',
            'line-width': 1.5,
          },
        })

        this.map.addLayer({
          id: LAYER_HUBS,
          type: 'circle',
          source: SOURCE_HUBS,
          filter: ['!=', ['get', 'icone'], 1],
          paint: {
            'circle-radius': ['case', ['==', ['get', 'badge'], 1], 10, 6],
            'circle-color': COR_HUB,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
          },
        })

        this.map.addLayer({
          id: LAYER_HUB_ICONS,
          type: 'symbol',
          source: SOURCE_HUBS,
          filter: ['==', ['get', 'icone'], 1],
          layout: {
            'icon-image': ['get', 'icon'],
            'icon-size': 0.5,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          },
        })

        this.map.addLayer({
          id: LAYER_HUB_LABELS,
          type: 'symbol',
          source: SOURCE_HUBS,
          filter: ['==', ['get', 'badge'], 1],
          layout: {
            'text-field': ['to-string', ['get', 'qtdSensores']],
            'text-size': 10,
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-allow-overlap': true,
            'text-ignore-placement': true,
          },
          paint: {
            'text-color': '#ffffff',
          },
        })

        this.map.addLayer({
          id: LAYER_LEAVES,
          type: 'symbol',
          source: SOURCE_LEAVES,
          layout: {
            'icon-image': ['get', 'icon'],
            'icon-size': 0.5,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          },
        })
      }
    },
    diferencaLongitude(a, b) {
      const delta = Math.abs(a - b) % 360
      return delta > 180 ? 360 - delta : delta
    },
    projetarHubVisivel(center) {
      const projected = this.map.project(center)
      if (!Number.isFinite(projected.x) || !Number.isFinite(projected.y)) {
        return null
      }

      const canvas = this.map.getCanvas()
      const margem = 80
      if (
        projected.x < -margem ||
        projected.y < -margem ||
        projected.x > canvas.width + margem ||
        projected.y > canvas.height + margem
      ) {
        return null
      }

      const idaVolta = this.map.unproject(projected)
      if (!Number.isFinite(idaVolta.lng) || !Number.isFinite(idaVolta.lat)) {
        return null
      }

      // No globo, project() de um ponto atrás da Terra cai na face visível.
      // Se o unproject não volta perto do hub, o ponto está do outro lado.
      if (
        this.diferencaLongitude(idaVolta.lng, center[0]) > 2 ||
        Math.abs(idaVolta.lat - center[1]) > 2
      ) {
        return null
      }

      return projected
    },
    cameraKey() {
      const center = this.map.getCenter()
      const canvas = this.map.getCanvas()

      return [
        this.map.getZoom(),
        this.map.getBearing(),
        this.map.getPitch(),
        center.lng,
        center.lat,
        canvas.width,
        canvas.height,
      ].join('|')
    },
    onMapRender() {
      if (!this.mapLoaded || !this.iconesProntos) {
        return
      }

      const nextKey = this.cameraKey()
      if (nextKey === this.lastCameraKey) {
        return
      }

      this.updateSpiderPositions()
    },
    rebuildSpiders() {
      if (!this.mapLoaded || !this.iconesProntos) {
        return
      }

      const idsAtuais = new Set(
        (this.equipamentos ?? []).map((equipamento) => String(equipamento.id))
      )
      Object.keys(this.aranhasRecolhidas).forEach((id) => {
        if (!idsAtuais.has(id)) {
          this.$delete(this.aranhasRecolhidas, id)
        }
      })

      this.spiderItems = this.buildSpiderItems()
      this.lastCameraKey = null
      this.updateSpiderPositions()
    },
    updateSpiderPositions() {
      const sourceHubs = this.map.getSource(SOURCE_HUBS)
      const sourceLegs = this.map.getSource(SOURCE_LEGS)
      const sourceLeaves = this.map.getSource(SOURCE_LEAVES)

      if (!sourceHubs || !sourceLegs || !sourceLeaves) {
        return
      }

      const hubs = emptyCollection()
      const legs = emptyCollection()
      const leaves = emptyCollection()
      const leafCoordsPorSensor = {}

      this.spiderItems.forEach((item) => {
        const projected = this.projetarHubVisivel(item.center)
        if (!projected) {
          return
        }

        const equipamentoId = String(item.hub.properties.equipamentoId)
        const recolhido = this.isAranhaRecolhida(equipamentoId) ? 1 : 0
        const semSensores = item.hub.properties.semSensores === 1 ? 1 : 0
        const badge = recolhido || semSensores ? 1 : 0
        const icone = !badge && !semSensores ? 1 : 0
        const folhaUnica = !recolhido && item.leaves.length === 1

        if (!folhaUnica) {
          hubs.features.push({
            ...item.hub,
            properties: {
              ...item.hub.properties,
              recolhido,
              semSensores,
              badge,
              icone,
              qtdSensores: item.leaves.length,
              icon: ID_ICONE_HUB_NEUTRO,
            },
          })
        }

        if (recolhido) {
          return
        }

        item.leaves.forEach((leaf) => {
          const leafLngLat = this.map.unproject([
            projected.x + leaf.dx,
            projected.y + leaf.dy,
          ])
          const leafCoords =
            Number.isFinite(leafLngLat.lng) && Number.isFinite(leafLngLat.lat)
              ? [leafLngLat.lng, leafLngLat.lat]
              : item.center

          if (leaf.dx !== 0 || leaf.dy !== 0) {
            legs.features.push({
              type: 'Feature',
              geometry: {
                type: 'LineString',
                coordinates: [item.center, leafCoords],
              },
              properties: leaf.legProperties,
            })
          }

          leaves.features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: leafCoords },
            properties: leaf.properties,
          })
          leafCoordsPorSensor[String(leaf.properties.sensorId)] = {
            coords: leafCoords,
            dx: leaf.dx,
            dy: leaf.dy,
          }
        })
      })

      this.leafCoordsPorSensor = leafCoordsPorSensor
      sourceHubs.setData(hubs)
      sourceLegs.setData(legs)
      sourceLeaves.setData(leaves)
      this.lastCameraKey = this.cameraKey()
      this.sincronizarSensorPopups()
      this.sincronizarOndasPendente()
    },
    criarElOndaPendente() {
      const el = document.createElement('div')
      el.className = 'acao-pendente-onda'
      const anel = document.createElement('span')
      anel.className = 'acao-pendente-onda__anel'
      const anelAtraso = document.createElement('span')
      anelAtraso.className =
        'acao-pendente-onda__anel acao-pendente-onda__anel--atraso'
      el.appendChild(anel)
      el.appendChild(anelAtraso)
      return el
    },
    sincronizarOndasPendente() {
      if (!this.map) {
        return
      }

      const visiveis = {}
      this.spiderItems.forEach((item) => {
        if (item.hub.properties.pendente !== 1) {
          return
        }
        if (!this.projetarHubVisivel(item.center)) {
          return
        }
        visiveis[String(item.hub.properties.equipamentoId)] = item.center
      })

      Object.keys(this.pendenteOndaMarkers).forEach((id) => {
        if (!visiveis[id]) {
          this.pendenteOndaMarkers[id].remove()
          delete this.pendenteOndaMarkers[id]
        }
      })

      Object.keys(visiveis).forEach((id) => {
        const coords = visiveis[id]
        let marker = this.pendenteOndaMarkers[id]
        if (!marker) {
          marker = new mapboxgl.Marker({
            element: this.criarElOndaPendente(),
            anchor: 'center',
          })
            .setLngLat(coords)
            .addTo(this.map)
          this.pendenteOndaMarkers[id] = marker
          return
        }
        marker.setLngLat(coords)
      })
    },
    removerOndasPendente() {
      Object.keys(this.pendenteOndaMarkers).forEach((id) => {
        this.pendenteOndaMarkers[id].remove()
      })
      this.pendenteOndaMarkers = {}
    },
    buildSpiderItems() {
      if (!this.equipamentosVisiveis) {
        return []
      }

      const pendentes = new Set(
        this.equipamentosComAcaoPendente.map((id) => String(id))
      )
      const items = []

      this.equipamentos.forEach((equipamento) => {
        const lng = Number(equipamento.longitude)
        const lat = Number(equipamento.latitude)
        const sensores = (equipamento.sensores ?? []).filter((sensor) =>
          this.iconeSensorVisivel(sensor)
        )

        if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
          return
        }

        const center = [lng, lat]
        const offsets =
          sensores.length > 0 ? this.calcularOffsets(sensores.length) : []

        items.push({
          center,
          hub: {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: center },
            properties: {
              equipamentoId: String(equipamento.id),
              nome: equipamento.nome,
              pendente: pendentes.has(String(equipamento.id)) ? 1 : 0,
              semSensores: (equipamento.sensores ?? []).length === 0 ? 1 : 0,
            },
          },
          leaves: sensores.map((sensor, index) => {
            const [dx, dy] = offsets[index] ?? [0, 0]

            return {
              dx,
              dy,
              properties: {
                equipamentoId: String(equipamento.id),
                sensorId: String(sensor.id),
                nome: equipamento.nome,
                titulo: sensor.descricao || sensor.tipoNome || '',
                tipoSensor: sensor.tipoSensor,
                tipoNome: sensor.tipoNome,
                icon: obterIdImagemSensor(
                  sensor.tipoNome,
                  this.valoresPorSensorId[String(sensor.id)]
                ),
              },
              legProperties: {
                equipamentoId: String(equipamento.id),
              },
            }
          }),
        })
      })

      return items
    },
    iconeSensorVisivel(sensor) {
      const icone = obterIconeSensor(sensor.tipoNome)
      return this.iconesSensorVisiveis[icone] === true
    },
    atualizarIconesSensorFolhas() {
      if (!this.mapLoaded || !this.iconesProntos) {
        return
      }

      let mudou = false
      this.spiderItems.forEach((item) => {
        item.leaves.forEach((leaf) => {
          const icon = obterIdImagemSensor(
            leaf.properties.tipoNome,
            this.valoresPorSensorId[String(leaf.properties.sensorId)]
          )
          if (leaf.properties.icon !== icon) {
            leaf.properties.icon = icon
            mudou = true
          }
        })
      })

      if (mudou) {
        this.updateSpiderPositions()
      }
    },
    calcularOffsets(count) {
      if (count === 1) {
        return [[0, 0]]
      }
      if (count < CIRCLE_SPIRAL_SWITCHOVER) {
        return this.calcularOffsetsCirculo(count)
      }

      return this.calcularOffsetsEspiral(count)
    },
    calcularOffsetsCirculo(count) {
      const offsets = []
      const step = (2 * Math.PI) / count
      const radius = Math.max(
        CIRCLE_RADIUS_MIN,
        (count * CIRCLE_POPUP_SLOT_PX) / (2 * Math.PI)
      )

      for (let i = 0; i < count; i += 1) {
        const angle = step * i
        const r =
          radius + (count >= 5 && i % 2 === 1 ? CIRCLE_RAIO_LONGO_EXTRA : 0)
        offsets.push([r * Math.cos(angle), r * Math.sin(angle)])
      }

      return offsets
    },
    calcularOffsetsEspiral(count) {
      const offsets = []
      let legLength = SPIRAL_LEG_LENGTH_START
      let angle = 0

      for (let i = 0; i < count; i += 1) {
        angle += SPIRAL_LEAVES_SEPARATION / legLength + 5e-4 * i
        offsets.push([legLength * Math.cos(angle), legLength * Math.sin(angle)])
        legLength += (2 * Math.PI * SPIRAL_LEG_LENGTH_FACTOR) / angle
      }

      return offsets
    },
    equipamentoPorId(equipamentoId) {
      return this.equipamentos.find(
        (equipamento) => String(equipamento.id) === String(equipamentoId)
      )
    },
    emitEquipamentoClick(equipamentoId) {
      const equipamento = this.equipamentoPorId(equipamentoId)
      if (equipamento) {
        this.$emit('marker-click', equipamento)
      }
    },
    handleLeafClick(event) {
      const feature = event.features?.[0]
      if (!feature) {
        return
      }

      this.emitEquipamentoClick(feature.properties.equipamentoId)
    },
    isAranhaRecolhida(equipamentoId) {
      return Boolean(this.aranhasRecolhidas[String(equipamentoId)])
    },
    toggleAranha(equipamentoId) {
      const id = String(equipamentoId)
      if (this.aranhasRecolhidas[id]) {
        this.$delete(this.aranhasRecolhidas, id)
      } else {
        this.$set(this.aranhasRecolhidas, id, true)
      }

      this.lastCameraKey = null
      this.updateSpiderPositions()
    },
    handleHubClick(event) {
      const feature = event.features?.[0]
      if (!feature) {
        return
      }

      const equipamentoId = feature.properties.equipamentoId
      const item = this.spiderItems.find(
        (spider) =>
          String(spider.hub.properties.equipamentoId) === String(equipamentoId)
      )
      if (!item || item.leaves.length === 0) {
        this.emitEquipamentoClick(equipamentoId)
        return
      }

      this.toggleAranha(equipamentoId)
    },
    handleHubEnter(event) {
      this.map.getCanvas().style.cursor = 'pointer'
      const feature = event.features?.[0]
      if (feature) {
        this.mostrarHubHoverPopup(feature)
      }
    },
    handleHubMove(event) {
      const feature = event.features?.[0]
      if (feature) {
        this.mostrarHubHoverPopup(feature)
      }
    },
    handleHubLeave() {
      this.map.getCanvas().style.cursor = ''
      if (this.buscaHubPopup) {
        return
      }
      this.esconderHubHoverPopup()
    },
    mostrarHubHoverPopup(feature) {
      const nome = feature.properties?.nome
      const equipamentoId = String(feature.properties?.equipamentoId || '')
      const coords = feature.geometry?.coordinates
      if (!nome || !coords) {
        return
      }

      if (this.hubHoverPopup && this.hubHoverEquipamentoId === equipamentoId) {
        this.hubHoverPopup.setLngLat(coords)
        return
      }

      this.esconderHubHoverPopup()

      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        closeOnMove: false,
        anchor: 'bottom',
        offset: 12,
        maxWidth: '260px',
        className: 'hub-popup-hover',
      })
      popup
        .setLngLat(coords)
        .setHTML(
          `<div class="hub-popup-hover__box">${this.escapeHtml(nome)}</div>`
        )
        .addTo(this.map)

      this.hubHoverPopup = popup
      this.hubHoverEquipamentoId = equipamentoId
    },
    esconderHubHoverPopup() {
      if (this.hubHoverPopup) {
        this.hubHoverPopup.remove()
        this.hubHoverPopup = null
      }
      this.hubHoverEquipamentoId = null
    },
    sincronizarSensorPopups() {
      if (!this.map) {
        return
      }

      const visiveis = this.leafCoordsPorSensor
      const idsVisiveis = new Set(Object.keys(visiveis))

      Object.keys(this.sensorPopups).forEach((sensorId) => {
        if (!idsVisiveis.has(sensorId)) {
          this.sensorPopups[sensorId].remove()
          delete this.sensorPopups[sensorId]
        }
      })

      idsVisiveis.forEach((sensorId) => {
        const info = visiveis[sensorId]
        const leaf = this.encontrarLeaf(sensorId)
        const coords = info?.coords
        const dx = info?.dx ?? leaf?.dx ?? 0
        const dy = info?.dy ?? leaf?.dy ?? 0
        const anchor = popupAnchorFromOffset(dx, dy)
        let popup = this.sensorPopups[sensorId]

        if (popup && popup.sensorAnchor !== anchor) {
          popup.remove()
          popup = null
          delete this.sensorPopups[sensorId]
        }

        if (!coords) {
          return
        }

        if (!popup) {
          popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            closeOnMove: false,
            anchor,
            offset: 10,
            maxWidth: '260px',
            className: 'sensor-popup-sempre',
          })
          popup.sensorAnchor = anchor
          this.sensorPopups[sensorId] = popup
          popup
            .setLngLat(coords)
            .setHTML(this.montarSensorPopupHtml(leaf?.properties, sensorId))
            .addTo(this.map)
          return
        }

        popup.setMaxWidth('260px')
        popup.setLngLat(coords)
      })
    },
    atualizarHtmlSensorPopups() {
      Object.keys(this.sensorPopups).forEach((sensorId) => {
        const leaf = this.encontrarLeaf(sensorId)
        this.sensorPopups[sensorId].setHTML(
          this.montarSensorPopupHtml(leaf?.properties, sensorId)
        )
      })
    },
    removerSensorPopups() {
      Object.keys(this.sensorPopups).forEach((sensorId) => {
        this.sensorPopups[sensorId].remove()
      })
      this.sensorPopups = {}
    },
    encontrarLeaf(sensorId) {
      for (let i = 0; i < this.spiderItems.length; i += 1) {
        const leaf = this.spiderItems[i].leaves.find(
          (item) => String(item.properties.sensorId) === String(sensorId)
        )
        if (leaf) {
          return leaf
        }
      }

      return null
    },
    montarSensorPopupHtml(props, sensorId) {
      const titulo = this.escapeHtml(props?.titulo || props?.nome || 'Sensor')
      const coleta = coletaSeRecente(
        this.valoresPorSensorId[String(props?.sensorId || sensorId)]
      )
      let valorTexto = 'Sem coleta'
      let horarioTexto = ''

      if (coleta && Number.isFinite(Number(coleta.valor))) {
        const icone = obterIconeSensor(props?.tipoNome)
        const sufixo =
          { oil_barrel: ' %', V: ' V', A: ' A', speed: ' mca' }[icone] || ''
        valorTexto = `${Number(coleta.valor).toFixed(2)}${sufixo}`
      } else if (coleta && coleta.valor != null) {
        valorTexto = String(coleta.valor)
      }

      if (coleta && coleta.horarioColeta) {
        horarioTexto = moment(coleta.horarioColeta).format('DD/MM HH:mm')
      }

      return `<div class="sensor-popup-sempre__box">
        <strong class="sensor-popup-sempre__titulo">${titulo}</strong>
        <div class="sensor-popup-sempre__valor">${this.escapeHtml(
          valorTexto
        )}</div>
        ${
          horarioTexto
            ? `<div class="sensor-popup-sempre__hora">${this.escapeHtml(
                horarioTexto
              )}</div>`
            : ''
        }
      </div>`
    },

    // ----- Camadas KML -------------------------------------------------------
    sourceIdCamada(id) {
      return `camada-${id}`
    },
    // Fonte derivada de pontos (centros das linhas) p/ os ícones de válvula.
    sourceIdCentros(id) {
      return `camada-centro-${id}`
    },
    // FeatureCollection de pontos no meio de cada LineString, herdando as
    // propriedades da linha (pro popup do ícone funcionar). Reservatório (só
    // pontos) resulta em coleção vazia — inofensivo.
    centrosDeLinhas(geojson) {
      const features = (geojson?.features || [])
        .filter((f) => f.geometry && f.geometry.type === 'LineString')
        .map((f) => {
          const meio = this.pontoMedioLinha(f.geometry.coordinates)
          return meio
            ? {
                type: 'Feature',
                geometry: { type: 'Point', coordinates: meio },
                properties: f.properties || {},
              }
            : null
        })
        .filter(Boolean)

      return { type: 'FeatureCollection', features }
    },
    // Ponto a 50% do comprimento da linha (segue as dobras, não só os extremos).
    pontoMedioLinha(coords) {
      if (!coords || coords.length === 0) return null
      if (coords.length === 1) return coords[0]

      const segs = []
      let total = 0
      for (let i = 1; i < coords.length; i++) {
        const d = this.distancia(coords[i - 1], coords[i])
        segs.push(d)
        total += d
      }
      if (total === 0) return coords[0]

      let alvo = total / 2
      for (let i = 0; i < segs.length; i++) {
        if (alvo <= segs[i]) {
          const t = segs[i] === 0 ? 0 : alvo / segs[i]
          const a = coords[i]
          const b = coords[i + 1]
          return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]
        }
        alvo -= segs[i]
      }
      return coords[coords.length - 1]
    },
    // Distância planar em graus — aproximação suficiente p/ achar o meio de um
    // link curto de válvula.
    distancia(a, b) {
      const dx = b[0] - a[0]
      const dy = b[1] - a[1]
      return Math.sqrt(dx * dx + dy * dy)
    },
    syncCamadas() {
      if (!this.map) return

      this.recalcularEscalaCota()
      this.recalcularEscalaDiametro()

      const visiveis = this.camadas.filter((c) => c.visivel && c.geojson)
      // String(): as chaves de camadaVersoes vêm de Object.keys (sempre string).
      // Os ids já chegam como string do backend (LongConverter), mas normalizamos
      // aqui para o has() nunca falhar por comparar number com string.
      const idsVisiveis = new Set(visiveis.map((c) => String(c.id)))

      // Remove do mapa as que sumiram ou foram ocultadas.
      Object.keys(this.camadaVersoes).forEach((id) => {
        if (!idsVisiveis.has(id)) {
          this.removerCamadaDoMapa(id)
        }
      })

      visiveis.forEach((camada) => {
        const sourceId = this.sourceIdCamada(camada.id)
        const source = this.map.getSource(sourceId)

        if (!source) {
          this.adicionarCamadaNoMapa(camada)
          return
        }

        if (this.camadaVersoes[camada.id] !== camada.versao) {
          source.setData(camada.geojson)
          // Mantém a fonte de centros (ícones de válvula) em sincronia.
          const centroSource = this.map.getSource(
            this.sourceIdCentros(camada.id)
          )
          if (centroSource) {
            centroSource.setData(this.centrosDeLinhas(camada.geojson))
          }
          this.camadaVersoes[camada.id] = camada.versao
        }
        if (this.camadaCores[camada.id] !== camada.cor) {
          this.atualizarCorNoMapa(camada.id, camada.cor)
        }
      })

      this.aplicarCorCotaNasJuncoes()
      this.aplicarCorDiametroNasTubulacoes()
      this.ordenarCamadasInp()
      this.trazerSpidersParaFrente()
    },
    recalcularEscalaCota() {
      this.escalaCota = faixaCotaNasCamadas(this.camadas)
    },
    recalcularEscalaDiametro() {
      const antes = Object.keys(coresDiametroSalvas(this.basemapEscuro)).length
      this.escalaDiametro = escalaDiametroNasCamadas(this.camadas, {
        escuro: this.basemapEscuro,
      })
      const depois = Object.keys(coresDiametroSalvas(this.basemapEscuro)).length
      if (depois !== antes) {
        this.escalasCoresRev += 1
      }
    },
    alterarCorCota(indice, hex) {
      const cores = this.coresCotaEfetivas.slice()
      if (indice < 0 || indice >= cores.length) {
        return
      }
      cores[indice] = hex
      gravarCoresCota(cores)
      this.escalasCoresRev += 1
      this.aplicarCorCotaNasJuncoes()
    },
    alterarCorDiametro(valor, hex) {
      gravarCorDiametro(formatarDiametro(valor), hex, this.basemapEscuro)
      this.recalcularEscalaDiametro()
      this.escalasCoresRev += 1
      this.aplicarCorDiametroNasTubulacoes()
    },
    restaurarCoresCota() {
      restaurarPadrao('cota')
      this.escalasCoresRev += 1
      this.aplicarCorCotaNasJuncoes()
    },
    restaurarCorCota(indice) {
      restaurarStopCota(indice, this.coresCotaPadrao)
      this.escalasCoresRev += 1
      this.aplicarCorCotaNasJuncoes()
    },
    corPontoCamada(camada, cor) {
      if (camada.chave === CHAVE_JUNCTIONS && this.escalaCota) {
        return expressaoCorCota(
          this.escalaCota.min,
          this.escalaCota.max,
          COR_MARCADOR_NEUTRO,
          coresCotaSalvas()
        )
      }
      return cor
    },
    corLinhaCamada(camada, cor) {
      // Tubulações e válvulas: linha colorida pela escala de diâmetro.
      if (ehChaveDiametro(camada.chave) && this.escalaDiametro) {
        return expressaoCorDiametro(this.escalaDiametro, COR_MARCADOR_NEUTRO)
      }
      return cor
    },
    aplicarCorCotaNasJuncoes() {
      if (!this.map) {
        return
      }

      ;(this.camadas || []).forEach((camada) => {
        if (camada.chave !== CHAVE_JUNCTIONS) {
          return
        }
        const layerId = `${this.sourceIdCamada(camada.id)}-point`
        if (!this.map.getLayer(layerId)) {
          return
        }
        this.map.setPaintProperty(
          layerId,
          'circle-color',
          this.corPontoCamada(camada, camada.cor || '#1565c0')
        )
      })
    },
    aplicarCorDiametroNasTubulacoes() {
      if (!this.map) {
        return
      }

      ;(this.camadas || []).forEach((camada) => {
        if (!ehChaveDiametro(camada.chave)) {
          return
        }
        const layerId = `${this.sourceIdCamada(camada.id)}-line`
        if (!this.map.getLayer(layerId)) {
          return
        }
        this.map.setPaintProperty(
          layerId,
          'line-color',
          this.corLinhaCamada(camada, camada.cor || '#1565c0')
        )
      })
    },
    formatarCota(valor) {
      return Number(valor).toFixed(1)
    },
    ordenarCamadasInp() {
      if (!this.map) {
        return
      }

      const visiveis = (this.camadas || []).filter(
        (c) => c.visivel && c.geojson && c.chave
      )
      const pipes = visiveis.filter((c) => c.chave === 'pipes')
      const valves = visiveis.filter((c) => c.chave === 'valves')
      const resto = visiveis.filter(
        (c) => c.chave !== 'pipes' && c.chave !== 'valves'
      )
      const sufixosLinha = ['-fill', '-line-casing', '-line', '-line-hit']
      const sufixosIcone = ['-symbol-point', '-symbol-centro']
      pipes.forEach((camada) => this.trazerCamadaParaFrente(camada.id))
      valves.forEach((camada) =>
        this.trazerCamadaParaFrente(camada.id, sufixosLinha)
      )
      resto.forEach((camada) => this.trazerCamadaParaFrente(camada.id))
      valves.forEach((camada) =>
        this.trazerCamadaParaFrente(camada.id, sufixosIcone)
      )
    },
    trazerCamadaParaFrente(id, sufixos) {
      const sourceId = this.sourceIdCamada(id)
      const lista = sufixos || [
        '-fill',
        '-line-casing',
        '-line',
        '-line-hit',
        '-point',
        '-symbol-point',
        '-symbol-centro',
      ]
      lista.forEach((sufixo) => {
        const layerId = `${sourceId}${sufixo}`
        if (this.map.getLayer(layerId)) {
          this.map.moveLayer(layerId)
        }
      })
    },
    adicionarCamadaNoMapa(camada) {
      const sourceId = this.sourceIdCamada(camada.id)
      const cor = camada.cor || '#1565c0'
      const casing = this.corCasing
      const iconeNome = this.iconeDaCamada(camada)
      const filtroLinha = [
        'in',
        ['geometry-type'],
        ['literal', ['LineString', 'Polygon']],
      ]

      this.map.addSource(sourceId, { type: 'geojson', data: camada.geojson })

      this.map.addLayer({
        id: `${sourceId}-fill`,
        type: 'fill',
        source: sourceId,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: { 'fill-color': cor, 'fill-opacity': 0.25 },
      })
      // Casing por baixo da linha colorida: desacopla a cor da camada do basemap
      // (água/parque/rodovia). A cor adapta ao fundo (claro/escuro) via corCasing.
      this.map.addLayer({
        id: `${sourceId}-line-casing`,
        type: 'line',
        source: sourceId,
        filter: filtroLinha,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': casing,
          'line-width': 5,
          'line-opacity': 0.9,
        },
      })
      this.map.addLayer({
        id: `${sourceId}-line`,
        type: 'line',
        source: sourceId,
        filter: filtroLinha,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': this.corLinhaCamada(camada, cor),
          'line-width': 3,
        },
      })
      const hitId = `${sourceId}-line-hit`
      this.map.addLayer({
        id: hitId,
        type: 'line',
        source: sourceId,
        filter: filtroLinha,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#000',
          'line-width': 14,
          'line-opacity': 0,
        },
      })
      this.map.on('mouseenter', hitId, this.handleCamadaHitEnter)
      this.map.on('mouseleave', hitId, this.handleCamadaHitLeave)

      if (iconeNome) {
        // Reservatório (ponto): símbolo direto na fonte. Ponto não duplica entre
        // tiles.
        this.map.addLayer({
          id: `${sourceId}-symbol-point`,
          type: 'symbol',
          source: sourceId,
          filter: ['==', ['geometry-type'], 'Point'],
          layout: {
            'icon-image': iconeNome,
            'icon-size': ICONE_ESCALA,
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
            // Sempre em pé (alinhado à tela), nunca girado pelo mapa.
            'icon-rotation-alignment': 'viewport',
          },
        })
        // Válvula (linha): ícone no ponto médio, numa fonte de PONTOS derivada.
        // Evita o symbol-placement:line-center, que duplica o ícone quando a
        // linha cruza o limite de um tile (cada pedaço ganha um "centro").
        const centroId = this.sourceIdCentros(camada.id)
        this.map.addSource(centroId, {
          type: 'geojson',
          data: this.centrosDeLinhas(camada.geojson),
        })
        this.map.addLayer({
          id: `${sourceId}-symbol-centro`,
          type: 'symbol',
          source: centroId,
          layout: {
            'icon-image': iconeNome,
            'icon-size': ICONE_ESCALA,
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
            // Em pé como o reservatório: o ícone não acompanha o ângulo da
            // linha (era o que deixava a válvula de ponta-cabeça no line-center).
            'icon-rotation-alignment': 'viewport',
          },
        })
      } else {
        this.map.addLayer({
          id: `${sourceId}-point`,
          type: 'circle',
          source: sourceId,
          filter: ['==', ['geometry-type'], 'Point'],
          paint: {
            'circle-radius': 6,
            'circle-color': this.corPontoCamada(camada, cor),
            // Halo com a mesma cor de casing (adapta ao fundo), para os pontos.
            'circle-stroke-width': 2,
            'circle-stroke-color': casing,
          },
        })
        const pontoId = `${sourceId}-point`
        this.map.on('mouseenter', pontoId, this.handleCamadaHitEnter)
        this.map.on('mouseleave', pontoId, this.handleCamadaHitLeave)
      }

      this.camadaVersoes[camada.id] = camada.versao
      this.camadaCores[camada.id] = cor
    },
    atualizarCorNoMapa(id, cor) {
      const sourceId = this.sourceIdCamada(id)
      const nova = cor || '#1565c0'
      const camada = (this.camadas || []).find(
        (c) => String(c.id) === String(id)
      )
      if (this.map.getLayer(`${sourceId}-fill`)) {
        this.map.setPaintProperty(`${sourceId}-fill`, 'fill-color', nova)
      }
      if (
        this.map.getLayer(`${sourceId}-line`) &&
        !ehChaveDiametro(camada?.chave)
      ) {
        this.map.setPaintProperty(`${sourceId}-line`, 'line-color', nova)
      }
      if (
        this.map.getLayer(`${sourceId}-point`) &&
        camada?.chave !== CHAVE_JUNCTIONS
      ) {
        this.map.setPaintProperty(`${sourceId}-point`, 'circle-color', nova)
      }
      this.camadaCores[id] = cor
    },
    handleCamadaHitEnter() {
      this.map.getCanvas().style.cursor = 'pointer'
    },
    handleCamadaHitLeave() {
      this.map.getCanvas().style.cursor = ''
    },
    removerCamadaDoMapa(id) {
      const sourceId = this.sourceIdCamada(id)
      const hitId = `${sourceId}-line-hit`
      const pontoId = `${sourceId}-point`
      this.map.off('mouseenter', hitId, this.handleCamadaHitEnter)
      this.map.off('mouseleave', hitId, this.handleCamadaHitLeave)
      this.map.off('mouseenter', pontoId, this.handleCamadaHitEnter)
      this.map.off('mouseleave', pontoId, this.handleCamadaHitLeave)
      ;[
        '-fill',
        '-line-casing',
        '-line',
        '-line-hit',
        '-point',
        '-symbol-point',
        '-symbol-centro',
      ].forEach((sufixo) => {
        const layerId = `${sourceId}${sufixo}`
        if (this.map.getLayer(layerId)) this.map.removeLayer(layerId)
      })
      if (this.map.getSource(sourceId)) this.map.removeSource(sourceId)
      // Fonte derivada dos centros (ícones de válvula), se existir.
      const centroId = this.sourceIdCentros(id)
      if (this.map.getSource(centroId)) this.map.removeSource(centroId)
      delete this.camadaVersoes[id]
      delete this.camadaCores[id]
    },
    trocarBasemap(value) {
      if (value === this.estilo || !this.map) return

      this.estilo = value
      this.map.setStyle(value)

      // setStyle descarta sources/layers customizados E as imagens dos ícones;
      // ao carregar o novo estilo, re-registramos os ícones e re-adicionamos as
      // camadas KML e o spider de sensores.
      //
      // Usamos 'style.load' — NÃO isStyleLoaded(): este só fica true quando todas
      // as sources do basemap terminaram de baixar os tiles (que disparam
      // 'sourcedata', não 'styledata'), então esperar por ele trava. 'style.load'
      // dispara assim que o estilo é parseado (antes dos tiles), que é exatamente
      // quando addSource/addLayer já são seguros.
      this.map.once('style.load', async () => {
        this.camadaVersoes = {}
        this.camadaCores = {}
        await this.carregarIcones()
        await registrarIconesSensores(this.map)
        this.syncCamadas()
        this.ensureLayers()
        this.rebuildSpiders()
        this.trazerSpidersParaFrente()
      })
    },
    trazerSpidersParaFrente() {
      ;[
        LAYER_LEGS,
        LAYER_HUBS,
        LAYER_HUB_ICONS,
        LAYER_HUB_LABELS,
        LAYER_LEAVES,
      ].forEach((layerId) => {
        if (this.map.getLayer(layerId)) {
          this.map.moveLayer(layerId)
        }
      })
    },
    layerIdsCamadas() {
      return Object.keys(this.camadaVersoes)
        .flatMap((id) => {
          const sourceId = this.sourceIdCamada(id)
          return [
            `${sourceId}-fill`,
            `${sourceId}-line`,
            `${sourceId}-line-hit`,
            `${sourceId}-point`,
            `${sourceId}-symbol-point`,
            `${sourceId}-symbol-centro`,
          ]
        })
        .filter((layerId) => this.map.getLayer(layerId))
    },
    // ----- Ícones de seção (válvula/reservatório) ----------------------------
    nomeImagem(chave) {
      return `inp-icone-${chave}`
    },
    // Nome da imagem do ícone da camada, ou null se a seção usa cor (padrão).
    iconeDaCamada(camada) {
      return chaveUsaIcone(camada.chave) ? this.nomeImagem(camada.chave) : null
    },
    // Registra as imagens dos ícones no estilo atual. Idempotente (hasImage);
    // chamado no load e a cada troca de basemap (setStyle descarta as imagens).
    carregarIcones() {
      return Promise.all(
        Object.entries(ICONE_POR_CHAVE).map(([chave, url]) =>
          this.adicionarIcone(this.nomeImagem(chave), url)
        )
      )
    },
    adicionarIcone(nome, url) {
      return new Promise((resolve) => {
        if (!this.map || this.map.hasImage(nome)) {
          resolve()
          return
        }
        const img = new Image()
        img.onload = () => {
          if (this.map && !this.map.hasImage(nome)) {
            this.map.addImage(nome, this.rasterizarIcone(img))
          }
          resolve()
        }
        // Se o ícone falhar ao carregar, segue sem símbolo (a camada some do mapa
        // até resolver, mas não quebra o resto).
        img.onerror = () => resolve()
        img.src = url
      })
    },
    // Rasteriza o SVG num canvas com halo claro em volta, pra o símbolo escuro
    // ler sobre qualquer basemap (rua/satélite/escuro). Devolve ImageData.
    rasterizarIcone(img) {
      const tamanho = ICONE_TAMANHO
      const margem = Math.round(tamanho * 0.14) // espaço pro halo não cortar
      const canvas = document.createElement('canvas')
      canvas.width = tamanho
      canvas.height = tamanho
      const ctx = canvas.getContext('2d')
      const alvo = tamanho - margem * 2

      ctx.shadowColor = 'rgba(255, 255, 255, 0.9)'
      ctx.shadowBlur = Math.round(tamanho * 0.09)
      // Duas passadas deixam o halo (glow do shadow) mais sólido.
      ctx.drawImage(img, margem, margem, alvo, alvo)
      ctx.drawImage(img, margem, margem, alvo, alvo)

      return ctx.getImageData(0, 0, tamanho, tamanho)
    },
    onMapaClick(event) {
      const layersSpider = [LAYER_HUBS, LAYER_HUB_ICONS, LAYER_LEAVES].filter(
        (layerId) => this.map.getLayer(layerId)
      )
      if (
        layersSpider.length > 0 &&
        this.map.queryRenderedFeatures(event.point, { layers: layersSpider })
          .length > 0
      ) {
        return
      }

      const layers = this.layerIdsCamadas()
      if (layers.length === 0) return

      const features = this.map.queryRenderedFeatures(event.point, { layers })
      if (features.length === 0) return

      const feature = features[0]
      const props = feature.properties || {}
      const html = this.montarPopup(props)
      if (!html) return

      this.abrirCamadaPopup(event.lngLat, html)
      this.completarPopupComDescricao(
        this.camadaIdDaSource(feature.source),
        props
      )
    },
    // source = `camada-{id}` ou `camada-centro-{id}`; modelo EPANET usa id
    // composto (epanet-modelo:pipes) e nao tem descricao sob demanda.
    camadaIdDaSource(source) {
      if (!source || typeof source !== 'string') return null
      const match = source.match(/^camada-(?:centro-)?(.+)$/)
      return match ? match[1] : null
    },
    abrirCamadaPopup(lngLat, html) {
      this.removerCamadaPopup()
      const popup = new mapboxgl.Popup({ maxWidth: '320px' })
        .setLngLat(lngLat)
        .setHTML(html)
        .addTo(this.map)
      popup.on('close', () => {
        if (this.camadaPopup === popup) {
          this.camadaPopup = null
        }
      })
      this.camadaPopup = popup
    },
    removerCamadaPopup() {
      if (this.camadaPopup) {
        this.camadaPopup.remove()
        this.camadaPopup = null
      }
    },
    // Monta o HTML do popup a partir das propriedades da feição (rotuladas pelo
    // backend): `nome` vira título; as demais chaves (exceto as que começam com
    // `_`, como `_d`) viram uma tabela chave/valor; `descricao` (HTML do KML,
    // quando ainda vem no payload do fallback) é sanitizada com DOMPurify e
    // renderizada como HTML por último.
    montarPopup(props, descricaoExtra) {
      const { nome, descricao, ...resto } = props || {}
      const partes = []

      if (nome) {
        partes.push(
          `<strong class="camada-popup__titulo">${this.escapeHtml(
            nome
          )}</strong>`
        )
      }

      const linhas = Object.entries(resto)
        .filter(
          ([chave, v]) =>
            !String(chave).startsWith('_') &&
            v !== null &&
            v !== undefined &&
            String(v).trim() !== ''
        )
        .map(
          ([chave, valor]) =>
            `<tr><th>${this.escapeHtml(chave)}</th><td>${this.escapeHtml(
              String(valor)
            )}</td></tr>`
        )
      if (linhas.length > 0) {
        partes.push(
          `<table class="camada-popup__props">${linhas.join('')}</table>`
        )
      }

      // descricao pode conter HTML (ex.: tabela do ArcGIS), então é renderizada
      // como HTML — mas sanitizada com DOMPurify antes do setHTML: o KML pode vir
      // de fonte não confiável e <script>/on*/javascript: seriam XSS no popup.
      const desc = descricaoExtra != null ? descricaoExtra : descricao
      if (desc) {
        const descricaoSegura = DOMPurify.sanitize(desc)
        if (descricaoSegura.trim() !== '') {
          partes.push(
            `<div class="camada-popup__desc">${descricaoSegura}</div>`
          )
        }
      }

      if (partes.length === 0) return null
      return `<div class="camada-popup">${partes.join('')}</div>`
    },
    // Se a feicao tem `_d` (indice), busca a descricao e completa o popup aberto.
    // Descarta a resposta se o popup ja foi fechado/trocado.
    async completarPopupComDescricao(camadaId, props) {
      if (
        this.localId == null ||
        camadaId == null ||
        props == null ||
        props._d == null ||
        props._d === ''
      ) {
        return
      }

      // Modelo EPANET / ids compostos nao tem endpoint de descricao.
      if (String(camadaId).includes(':') || !/^\d+$/.test(String(camadaId))) {
        return
      }

      const popup = this.camadaPopup
      const indice = Number(props._d)
      if (!Number.isFinite(indice) || indice < 0) return

      const resposta = await obterDescricaoFeicao(
        this.localId,
        camadaId,
        indice
      )
      if (this.camadaPopup !== popup || !popup) return

      const descricao = resposta && resposta.descricao
      if (!descricao) return

      const html = this.montarPopup(props, descricao)
      if (html) {
        popup.setHTML(html)
      }
    },
    escapeHtml(texto) {
      const div = document.createElement('div')
      div.textContent = texto
      return div.innerHTML
    },

    // ----- Enquadramento -----------------------------------------------------
    temCamadaVisivel() {
      return this.camadas.some((c) => c.visivel && c.geojson)
    },
    fitContentSeNecessario() {
      if (this.fitFeito) return
      if (this.equipamentos.length === 0 && !this.temCamadaVisivel()) return

      this.fitToContent()
      this.fitFeito = true
    },
    andarCoordenadas(geometry, callback) {
      const walk = (coords) => {
        if (typeof coords[0] === 'number') {
          callback(coords)
          return
        }
        coords.forEach(walk)
      }
      if (geometry && geometry.coordinates) walk(geometry.coordinates)
    },
    fitToContent() {
      const bounds = new mapboxgl.LngLatBounds()
      let temAlgo = false

      this.equipamentos.forEach((equipamento) => {
        bounds.extend([
          Number(equipamento.longitude),
          Number(equipamento.latitude),
        ])
        temAlgo = true
      })

      this.camadas
        .filter((c) => c.visivel && c.geojson)
        .forEach((camada) => {
          camada.geojson.features.forEach((feature) => {
            this.andarCoordenadas(feature.geometry, (coord) => {
              bounds.extend(coord)
              temAlgo = true
            })
          })
        })

      if (!temAlgo) return

      if (this.equipamentos.length === 1 && !this.temCamadaVisivel()) {
        const equipamento = this.equipamentos[0]
        this.map.flyTo({
          center: [Number(equipamento.longitude), Number(equipamento.latitude)],
          zoom: 14,
        })
        return
      }

      this.map.fitBounds(bounds, { padding: 80, maxZoom: 15 })
    },
    focarPonto(lng, lat, opcoes) {
      this.focarAlvo({
        lng,
        lat,
        ...(opcoes || {}),
      })
    },
    focarAlvo(alvo) {
      if (!this.map || !alvo) {
        return
      }

      const lng = Number(alvo.lng)
      const lat = Number(alvo.lat)
      if (Number.isNaN(lng) || Number.isNaN(lat)) {
        return
      }

      this.limparDestaqueBusca()
      this.prepararAranhaDoAlvo(alvo)
      const seq = this.buscaDestaqueSeq

      const aposVoo = () => {
        if (seq !== this.buscaDestaqueSeq) {
          return
        }
        if (!this.cameraNoAlvo(lng, lat)) {
          this.buscaDestaqueOnMoveEnd = aposVoo
          this.map.once('moveend', aposVoo)
          return
        }
        this.buscaDestaqueOnMoveEnd = null
        this.mostrarDestaqueAposVoo(alvo, lng, lat, seq, 0)
      }

      const comecarFlyTo = () => {
        if (seq !== this.buscaDestaqueSeq) {
          return
        }
        if (this.cameraNoAlvo(lng, lat)) {
          aposVoo()
          return
        }
        this.buscaDestaqueOnMoveEnd = aposVoo
        this.map.once('moveend', aposVoo)
        this.map.flyTo({
          center: [lng, lat],
          zoom: 16,
        })
      }

      if (this.map.isMoving()) {
        const aposParar = () => {
          if (seq !== this.buscaDestaqueSeq) {
            return
          }
          comecarFlyTo()
        }
        this.buscaDestaqueOnMoveEnd = aposParar
        this.map.once('moveend', aposParar)
        this.map.stop()
        if (!this.map.isMoving()) {
          this.map.off('moveend', aposParar)
          this.buscaDestaqueOnMoveEnd = null
          comecarFlyTo()
        }
        return
      }

      comecarFlyTo()
    },
    cameraNoAlvo(lng, lat) {
      if (!this.map || this.map.isMoving()) {
        return false
      }

      const centro = this.map.getCenter()
      return (
        Math.abs(centro.lng - lng) < 1e-4 &&
        Math.abs(centro.lat - lat) < 1e-4 &&
        Math.abs(this.map.getZoom() - 16) < 0.2
      )
    },
    prepararAranhaDoAlvo(alvo) {
      if (alvo.sensorId == null || alvo.equipamentoId == null) {
        return
      }

      const id = String(alvo.equipamentoId)
      if (this.aranhasRecolhidas[id]) {
        this.$delete(this.aranhasRecolhidas, id)
      }

      this.rebuildSpiders()
    },
    mostrarDestaqueAposVoo(alvo, lng, lat, seq, tentativa) {
      if (seq !== this.buscaDestaqueSeq) {
        return
      }

      const folha = this.pontoFolhaDoSensor(alvo.sensorId)
      if (alvo.sensorId != null && !folha && tentativa < 5) {
        this.rebuildSpiders()
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.mostrarDestaqueAposVoo(alvo, lng, lat, seq, tentativa + 1)
          })
        })
        return
      }

      this.mostrarDestaqueBusca({
        lng: folha ? folha[0] : lng,
        lat: folha ? folha[1] : lat,
        titulo: alvo.titulo,
        geometry: alvo.geometry,
        properties: alvo.properties,
        chave: alvo.chave,
        camadaId: alvo.camadaId,
        sensorId: alvo.sensorId,
        equipamentoId: alvo.equipamentoId,
      })
    },
    pontoFolhaDoSensor(sensorId) {
      if (sensorId == null || !this.map) {
        return null
      }

      this.updateSpiderPositions()
      const info = this.leafCoordsPorSensor[String(sensorId)]
      const coords = info && info.coords
      if (
        !coords ||
        !Number.isFinite(coords[0]) ||
        !Number.isFinite(coords[1])
      ) {
        return null
      }

      return coords
    },
    mostrarDestaqueBusca({
      lng,
      lat,
      titulo,
      geometry,
      properties,
      chave,
      camadaId,
      sensorId,
      equipamentoId,
    }) {
      if (this.buscaDestaqueOnMoveEnd && this.map) {
        this.map.off('moveend', this.buscaDestaqueOnMoveEnd)
        this.buscaDestaqueOnMoveEnd = null
      }
      if (!this.map) {
        return
      }
      if (
        this.buscaDestaqueMarker ||
        this.map.getSource(SOURCE_BUSCA_DESTAQUE)
      ) {
        return
      }

      const tipo = geometry && geometry.type
      const ehValvula = chave === 'valves'
      let destaqueLng = lng
      let destaqueLat = lat
      if (ehValvula && tipo === 'LineString') {
        const meio = this.pontoMedioLinha(geometry.coordinates)
        if (meio) {
          destaqueLng = meio[0]
          destaqueLat = meio[1]
        }
      }

      const ehLinha =
        tipo === 'LineString' ||
        tipo === 'MultiLineString' ||
        tipo === 'Polygon' ||
        tipo === 'MultiPolygon'

      if (ehLinha) {
        this.desenharLinhaDestaqueBusca(geometry)
      }
      if (ehValvula || !ehLinha) {
        this.desenharPulsoDestaqueBusca(destaqueLng, destaqueLat)
      }

      if (sensorId == null && equipamentoId != null) {
        this.mostrarHubHoverPopup({
          properties: {
            nome: titulo,
            equipamentoId: String(equipamentoId),
          },
          geometry: { coordinates: [destaqueLng, destaqueLat] },
        })
        this.buscaHubPopup = true
      } else if (sensorId == null && properties) {
        const html = this.montarPopup(properties)
        if (html) {
          this.abrirCamadaPopup([destaqueLng, destaqueLat], html)
          this.completarPopupComDescricao(camadaId, properties)
        }
      }

      this.buscaDestaqueTimer = setTimeout(() => {
        this.limparDestaqueBusca()
      }, DESTAQUE_BUSCA_MS)
    },
    desenharLinhaDestaqueBusca(geometry) {
      this.removerCamadaDestaqueBusca()
      this.map.addSource(SOURCE_BUSCA_DESTAQUE, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry,
          properties: {},
        },
      })

      if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
        this.map.addLayer({
          id: LAYER_BUSCA_DESTAQUE_FILL,
          type: 'fill',
          source: SOURCE_BUSCA_DESTAQUE,
          paint: {
            'fill-color': '#ffeb3b',
            'fill-opacity': 0.25,
          },
        })
      }

      this.map.addLayer({
        id: LAYER_BUSCA_DESTAQUE_CASING,
        type: 'line',
        source: SOURCE_BUSCA_DESTAQUE,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#333',
          'line-width': 8,
          'line-opacity': 0.9,
        },
      })
      this.map.addLayer({
        id: LAYER_BUSCA_DESTAQUE_LINE,
        type: 'line',
        source: SOURCE_BUSCA_DESTAQUE,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#ffeb3b',
          'line-width': 4,
        },
      })
    },
    desenharPulsoDestaqueBusca(lng, lat) {
      const el = document.createElement('div')
      el.className = 'busca-destaque-pulso'
      const anel = document.createElement('span')
      anel.className = 'busca-destaque-pulso__anel'
      const anelAtraso = document.createElement('span')
      anelAtraso.className =
        'busca-destaque-pulso__anel busca-destaque-pulso__anel--atraso'
      const ponto = document.createElement('span')
      ponto.className = 'busca-destaque-pulso__ponto'
      el.appendChild(anel)
      el.appendChild(anelAtraso)
      el.appendChild(ponto)
      this.buscaDestaqueMarker = new mapboxgl.Marker({
        element: el,
        anchor: 'center',
      })
        .setLngLat([lng, lat])
        .addTo(this.map)
    },
    removerCamadaDestaqueBusca() {
      if (!this.map) {
        return
      }
      ;[
        LAYER_BUSCA_DESTAQUE_FILL,
        LAYER_BUSCA_DESTAQUE_LINE,
        LAYER_BUSCA_DESTAQUE_CASING,
      ].forEach((id) => {
        if (this.map.getLayer(id)) {
          this.map.removeLayer(id)
        }
      })
      if (this.map.getSource(SOURCE_BUSCA_DESTAQUE)) {
        this.map.removeSource(SOURCE_BUSCA_DESTAQUE)
      }
    },
    limparDestaqueBusca() {
      this.buscaDestaqueSeq += 1
      if (this.buscaDestaqueTimer) {
        clearTimeout(this.buscaDestaqueTimer)
        this.buscaDestaqueTimer = null
      }
      if (this.buscaDestaqueOnMoveEnd && this.map) {
        this.map.off('moveend', this.buscaDestaqueOnMoveEnd)
        this.buscaDestaqueOnMoveEnd = null
      }
      if (this.buscaDestaqueMarker) {
        this.buscaDestaqueMarker.remove()
        this.buscaDestaqueMarker = null
      }
      if (this.buscaHubPopup) {
        this.buscaHubPopup = false
        this.esconderHubHoverPopup()
      }
      this.removerCamadaDestaqueBusca()
    },
  },
}
</script>

<style lang="scss">
.sensor-popup-sempre {
  pointer-events: none;

  .mapboxgl-popup-content {
    padding: 4px 6px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  }

  &.mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip {
    border-top-color: #fff;
  }

  &.mapboxgl-popup-anchor-top .mapboxgl-popup-tip {
    border-bottom-color: #fff;
  }

  &.mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
    border-right-color: #fff;
  }

  &.mapboxgl-popup-anchor-right .mapboxgl-popup-tip {
    border-left-color: #fff;
  }

  &.mapboxgl-popup-anchor-bottom-left .mapboxgl-popup-tip,
  &.mapboxgl-popup-anchor-bottom-right .mapboxgl-popup-tip {
    border-top-color: #fff;
  }

  &.mapboxgl-popup-anchor-top-left .mapboxgl-popup-tip,
  &.mapboxgl-popup-anchor-top-right .mapboxgl-popup-tip {
    border-bottom-color: #fff;
  }

  &__box {
    font-size: 11px;
    line-height: 1.3;
    color: #333;
    max-width: 240px;
  }

  &__titulo {
    display: block;
    font-weight: 600;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  &__valor {
    margin-top: 2px;
  }

  &__hora {
    margin-top: 1px;
    color: #757575;
    font-size: 10px;
  }
}

.hub-popup-hover {
  pointer-events: none;

  .mapboxgl-popup-content {
    padding: 4px 8px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  }

  &.mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip {
    border-top-color: #fff;
  }

  &__box {
    font-size: 12px;
    font-weight: 600;
    line-height: 1.3;
    color: #333;
    max-width: 240px;
    overflow-wrap: anywhere;
  }
}

.camada-popup {
  font-size: 13px;
  max-height: 240px;
  overflow: auto;

  &__titulo {
    display: block;
  }

  // Tabela de metadados (campos rotulados pelo backend): rótulo à esquerda em
  // cinza, valor à direita. Sem bordas — leitura de "ficha", não de planilha.
  &__props {
    margin-top: 6px;
    border-collapse: collapse;
    width: 100%;

    th,
    td {
      padding: 2px 0;
      text-align: left;
      vertical-align: top;
    }

    th {
      padding-right: 12px;
      color: #757575;
      font-weight: 500;
      white-space: nowrap;
    }
  }

  &__desc {
    margin-top: 6px;

    table {
      border-collapse: collapse;
    }

    td,
    th {
      padding: 2px 6px;
      border: 1px solid #ddd;
    }
  }
}

.acao-pendente-onda {
  position: relative;
  width: 32px;
  height: 32px;
  pointer-events: none;
}

.acao-pendente-onda__anel {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 32px;
  height: 32px;
  margin: -16px 0 0 -16px;
  border-radius: 50%;
  border: 3px solid #ff9800;
  box-shadow: 0 0 0 2px #111, 0 0 0 3px #fff;
  box-sizing: border-box;
  animation: equipamento-marker-pulse 1.2s ease-out infinite;
}

.acao-pendente-onda__anel--atraso {
  animation-delay: 0.6s;
  animation-fill-mode: backwards;
}

.busca-destaque-pulso {
  position: relative;
  width: 32px;
  height: 32px;
  pointer-events: none;
}

.busca-destaque-pulso__ponto {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 12px;
  height: 12px;
  margin: -6px 0 0 -6px;
  border-radius: 50%;
  background: #ffeb3b;
  border: 2px solid #111;
  box-shadow: 0 0 0 2px #fff;
  box-sizing: border-box;
}

.busca-destaque-pulso__anel {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 32px;
  height: 32px;
  margin: -16px 0 0 -16px;
  border-radius: 50%;
  border: 3px solid #ffeb3b;
  box-shadow: 0 0 0 2px #111, 0 0 0 3px #fff;
  box-sizing: border-box;
  animation: equipamento-marker-pulse 1.2s ease-out infinite;
}

.busca-destaque-pulso__anel--atraso {
  animation-delay: 0.6s;
  animation-fill-mode: backwards;
}

@keyframes equipamento-marker-pulse {
  0% {
    transform: scale(0.45);
    opacity: 1;
  }
  100% {
    transform: scale(1.7);
    opacity: 0;
  }
}

// Alinha o controle de zoom do Mapbox à linguagem "chip de mapa" dos demais
// flutuantes (branco, raio 6px, sombra rasa). Por padrão ele vem com raio 4px e
// o anel de sombra próprios do Mapbox, destoando dos outros controles. Os
// controles são criados em runtime (sem data-v), por isso o bloco é não-scoped;
// o #mapContainerEquipamentos restringe o override ao mapa deste componente.
#mapContainerEquipamentos {
  .mapboxgl-ctrl-group {
    border-radius: 6px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  }

  .mapboxgl-ctrl-group button:first-child {
    border-radius: 6px 6px 0 0;
  }

  .mapboxgl-ctrl-group button:last-child {
    border-radius: 0 0 6px 6px;
  }
}
</style>

<style scoped lang="scss">
.mapa-wrapper {
  position: relative;
  height: 100%;
  min-height: 100%;
}

.fullHeight {
  min-height: 100%;
  height: 100%;
}

.legendas-escala {
  position: absolute;
  bottom: 24px;
  left: 12px;
  z-index: 5;
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.legenda-pressao {
  padding: 6px 8px;
  background: #f5f5f5;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  font-size: 12px;
  line-height: 1.4;
  color: #333;

  &__cabecalho {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 4px;
  }

  &__titulo {
    font-weight: 600;
    margin-bottom: 2px;
  }

  &__restaurar {
    margin: -2px -4px 0 0;
    padding: 0;
    width: 18px;
    height: 18px;
    border: none;
    background: none;
    color: #1565c0;
    font-family: 'Material Icons';
    font-size: 16px;
    line-height: 18px;
    cursor: pointer;
    flex: 0 0 auto;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 6px;

    .legenda-pressao__restaurar {
      margin: 0 0 0 auto;
    }
  }

  &__swatch,
  &__swatch-cor {
    width: 0.7rem;
    height: 0.7rem;
    border-radius: 50%;
    border: 1px solid grey;
    flex: 0 0 auto;
  }

  &__swatch-cor {
    padding: 0;
    background: none;
    cursor: pointer;
    box-sizing: border-box;

    &::-webkit-color-swatch-wrapper {
      padding: 0;
    }

    &::-webkit-color-swatch {
      border: none;
      border-radius: 50%;
    }

    &::-moz-color-swatch {
      border: none;
      border-radius: 50%;
    }

    &:hover,
    &:focus-visible {
      transform: scale(1.2);
      box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.35);
      outline: none;
    }
  }

  &__onda {
    width: 0.7rem;
    height: 0.7rem;
    border-radius: 50%;
    border: 2px solid #ff9800;
    background: transparent;
    box-sizing: border-box;
    flex: 0 0 auto;
  }
}

.basemap-toggle {
  position: absolute;
  bottom: 24px;
  right: 12px;
  z-index: 5;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);

  &__select {
    border: none;
    background: #fff;
    color: #333;
    font-size: 12px;
    padding: 5px 10px;
    border-radius: 6px;
    cursor: pointer;
  }
}
</style>
